/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.25.0
 * @since 1.14.1
 */

import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { TableFooterNameType } from '@/main/components/Table/types/tableTypes';
import { EOnboardingComponentsId } from '@/main/modules/onboarding/types/onboardingTypes';

import { icons } from '../../../../../assets';
import { appHistoryPush } from '../../../../../modules/app/appHistory';
import { ELogBusinessEventName, ELogBusinessEventType } from '../../../../../modules/logging/loggingConstants';
import { AccessComponentKeyEnum } from '../../../../../modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponents } from '../../../../../modules/role/selectors/guardSelector';
import { wizardStorageClearStepsAction } from '../../../../../modules/wizard/wizardActions';
import { EWizardName } from '../../../../../modules/wizard/wizardConstants';
import { PageBillsType } from '../../../../../pages/PageBills/pageBillsTypes';
import {
    getWizardBillsDocUrl,
    isWizardBillsDocExternalUrl,
} from '../../../../../pages/WizardBillsDoc/utils/getWizardBillsDocUrl';
import { openExternalRoute } from '../../../../../routes/constants/externalRoutes';
import { IRouteParams } from '../../../../../routes/constants/routesConstants';
import {
    TTableButtonsGetTranslation,
    TTableButtonsLogBusinessEvent,
    TUseTableFooterButtonsOnboarding,
} from '../useTableFooterButtons';

const useTableFooterButtonWizardBillsDoc = (
    getTranslation: TTableButtonsGetTranslation,
    logBusinessEvent: TTableButtonsLogBusinessEvent,
    tableFooterName: TableFooterNameType,
) => {
    const { clientId } = useParams<IRouteParams>();
    const dispatch = useDispatch();

    const componentFeatures = useSelector(makeSelectGuardComponents());
    return {
        className: 'table-footer__link bills-doc',
        dataName: 'bills-document',
        icon: icons.archiveIcon,
        id: AccessComponentKeyEnum.FooterButtonBillsDoc,
        ...mapTableFooterNameToOnboarding[tableFooterName],
        ...getTranslation(AccessComponentKeyEnum.FooterButtonBillsDoc),
        onClick: useCallback(
            (type: PageBillsType) => {
                logBusinessEvent(
                    ELogBusinessEventName.USER_BILLS_REPORTS_MASTER_TO_STEP_1,
                    ELogBusinessEventType.common,
                );
                dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.BILLS_DOC }));

                const isUnpaid = type === PageBillsType.BILLS_UNPAID || type === PageBillsType.BILLS_UNPAID_ACCOUNT;
                const url = getWizardBillsDocUrl(componentFeatures, isUnpaid, clientId);
                isWizardBillsDocExternalUrl(componentFeatures) ? openExternalRoute(url) : appHistoryPush(url);
            },
            [componentFeatures, clientId, dispatch, logBusinessEvent],
        ),
    };
};

const mapTableFooterNameToOnboarding: TUseTableFooterButtonsOnboarding = {
    [TableFooterNameType.bills]: {
        onboarding: EOnboardingComponentsId.billsTableFooterButtonBillsDoc,
    },
    [TableFooterNameType.billsUnpaid]: {
        onboarding: EOnboardingComponentsId.billsTableFooterButtonBillsDoc,
    },
    [TableFooterNameType.billsAccountId]: {
        onboarding: EOnboardingComponentsId.billsTableFooterButtonBillsDoc,
    },
    [TableFooterNameType.billsUnpaidAccountId]: {
        onboarding: EOnboardingComponentsId.billsTableFooterButtonBillsDoc,
    },
};

export default useTableFooterButtonWizardBillsDoc;
